import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { CustomModal, toast } from "../components";
import localStorageKey from "../enumerations/localStorageKey";
import queryKey from "../enumerations/queryKey";
import { useFacebookPixel } from "../hooks/fbPixel/fbPixel.hooks";
import { GamePopup } from "../pages/Home/components";
import { useRecentPlayGames } from "../pages/Home/context/RecentPlayGamesContext";
import gameListsEnum from "../pages/Home/enumeration/gameListsEnum";
import { useRedirectToGame } from "../pages/Home/hooks/home.hook";
import {
  useGetAesexyGameLoginLink,
  useGetAgGameLoginLink,
  useGetAgTrialGameLink,
  useGetOmgGameLoginLink,
  useGetOmgDemoGameLink,
  useGetCQ9GameLoginLink,
  useGetCRSPORTGameLoginLink,
  useGetEvoGameLoginLink,
  useGetEvoplayGameLoginLink,
  useGetFBSPORTGameLoginLink,
  useGetFCFishGameLoginLink,
  useGetFCGameLoginLink,
  useGetFFFLiveGameLoginLink,
  useGetJDBFishGameLoginLink,
  useGetJDBSlotGameLoginLink,
  useGetJiliGameLoginLink,
  useGetJollySportGameLoginLink,
  useGetLuckySportGameLoginLink,
  useGetMgGameLoginLink,
  useGetMiniGameGameLoginLink,
  useGetONEEVOLIVEGameLoginLink,
  useGetONEGLOBALGameLoginLink,
  useGetONEHACKSAWGameLoginLink,
  useGetONEJDBFISHGameLoginLink,
  useGetONEJDBSLOTGameLoginLink,
  useGetONEJILICARDGameLoginLink,
  useGetONEJILICASINOGameLoginLink,
  useGetONEJILIFISHGameLoginLink,
  useGetONEJILISLOTGameLoginLink,
  useGetONEPGGameLoginLink,
  useGetONEPPGameLoginLink,
  useGetONEPPLIVEGameLoginLink,
  useGetONESABAGameLoginLink,
  useGetPPLiveGameLoginLink,
  useGetPTGameLoginLink,
  useGetPgGameLoginLink,
  useGetPlayStarGameLoginLink,
  useGetPpGameLoginLink,
  useGetSBOSPORTGameLoginLink,
  useGetSpribeGameLoginLink,
  useGetSpribeoneGameLoginLink,
  useGetT1GameLoginLink,
  useGetTcgGameLoginLink,
  useGetViaCasinoLiveGameLoginLink,
  useGetWCFGameLoginLink,
  useGetWeblockchainGameLoginLink,
  useGetWefishGameLoginLink,
  useGetWeliveGameLoginLink,
  useGetWelotteryGameLoginLink,
  useGetWeslotGameLoginLink,
  useGetWesportGameLoginLink,
  useGetYbslotGameLoginLink,
  useGetYggGameLoginLink,
  useGetYoubetGameLoginLink,
} from "../pages/Home/hooks/home1bbet.hooks";
import { safeToggleChatwootBubble } from "../plugins/safeChatwootBubble";
import { envChangeType, envType } from "../plugins/utils";
import { useLogin } from "./LoginContext";
import { useLoading } from "./modules/LoadingContext";

// 未登入試玩 OMG 遊戲目前只開放 aaslots (bbvnd)、ball188vnd
// versionConfig 由試算表產生，本機改會被 yarn start 重抓蓋掉，所以站點直接寫在這裡
// ball188 各幣別站共用 PRODUCTION_NAME_DEV，只能再用預設幣別區分
const OMG_DEMO_SITES = [{ name: "bbvnd" }, { name: "ball188", currency: "vnd" }];
export const isOmgDemoGame = (game3thCode) =>
  OMG_DEMO_SITES.some(
    (site) =>
      site.name === process.env.REACT_APP_PRODUCTION_NAME_DEV &&
      (!site.currency || site.currency === process.env.REACT_APP_DEFAULT_CURRENCY?.toLowerCase()),
  ) && !!game3thCode?.startsWith("omg");

// 未登入試玩 AG 遊戲，由試算表 AG_TRIAL_ENABLE 控制 (true / 1 開啟)，後端該站也要開 AG_TRIAL_ENABLE
export const isAgDemoGame = (game3thCode) =>
  ["true", "1"].includes(process.env.REACT_APP_AG_TRIAL_ENABLE) && !!game3thCode?.startsWith("ag");

export const isDemoGame = (game3thCode) => isOmgDemoGame(game3thCode) || isAgDemoGame(game3thCode);

const GameLoginContext = createContext({
  showGamePopup: false,
  setShowGamePopup: () => {},
  handleLoginGame: ({ isPopup = false, isFromSearch = false, game3th = {}, gameItem = "" }) => {},
  handleDemoGame: ({ game3th = {}, gameItem = {} }) => {},
});

export const GameLoginContextProvider = ({ children }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const cache = useQueryClient();
  const navigate = useNavigate();
  const getLanguage = () => i18n.language;
  const { setIsLoading } = useLoading();
  const { setRecentPlayGameList } = useRecentPlayGames();
  const { userInfo, checkIfLogin } = useLogin();
  const nowLang = getLanguage();
  const currency = localStorage.getItem(localStorageKey.DEFAULT_CURRENCY);
  const token = window.localStorage.getItem("token");
  // const { showTawkWidget, hideTawkWidget, tawkLoaded } = useTawk();

  const [mgGameCode, setMGGameCode] = useState(null);
  const [gameURL, setGameURL] = useState("");
  const [gameHTML, setGameHTML] = useState("");
  const [showHeader, setShowHeader] = useState(true);
  const [showBubble, setShowBubble] = useState(false);
  const [showGamePopup, setShowGamePopup] = useState(false);

  //saba
  const { mutate: redirectToGame } = useRedirectToGame();
  // Jolly Sport
  const { mutate: redirectToJollySport } = useGetJollySportGameLoginLink();
  //T1
  const { mutate: redirectToT1 } = useGetT1GameLoginLink();
  //Evo Live
  const { mutate: redirectToEvo } = useGetEvoGameLoginLink();
  //PP slot
  const { mutate: redirectToPp } = useGetPpGameLoginLink();
  //PG
  const { mutate: redirectToPg } = useGetPgGameLoginLink();
  //YGG
  const { mutate: redirectToYgg } = useGetYggGameLoginLink();
  //EVOPLAY
  const { mutate: redirectToEvoplay } = useGetEvoplayGameLoginLink();
  //JILI
  const { mutate: redirectToJili } = useGetJiliGameLoginLink();
  //SPRIBE
  const { mutate: redirectToSpribe } = useGetSpribeGameLoginLink();
  //PT
  const { mutate: redirectToPT } = useGetPTGameLoginLink();
  //PPLIVE
  const { mutate: redirectToPPLive } = useGetPPLiveGameLoginLink();
  //FC
  const { mutate: redirectToFC } = useGetFCGameLoginLink();
  //CQ9
  const { mutate: redirectToCQ9 } = useGetCQ9GameLoginLink();
  //PLAY STAR
  const { mutate: redirectToPlaystar } = useGetPlayStarGameLoginLink();
  //WE LIVE
  const { mutate: redirectToWeLive } = useGetWeliveGameLoginLink();
  //WE LOTTERY
  const { mutate: redirectToWeLottery } = useGetWelotteryGameLoginLink();
  //WE BLOCKCHAIN
  const { mutate: redirectToWeBlockchain } = useGetWeblockchainGameLoginLink();
  //TCG
  const { mutate: redirectToTCG } = useGetTcgGameLoginLink();
  //YOUBET
  const { mutate: redirectToYoubet } = useGetYoubetGameLoginLink();
  //AESEXY
  const { mutate: redirectToAeSexy } = useGetAesexyGameLoginLink();
  //SPRIBEONE
  const { mutate: redirectToSpribeone } = useGetSpribeoneGameLoginLink();
  //WESPORT
  const { mutate: redirectToWesport } = useGetWesportGameLoginLink();
  //LUCKYSPORT
  const { mutate: redirectToLuckySport } = useGetLuckySportGameLoginLink();
  //YBSLOT
  const { mutate: redirectToYBSlot } = useGetYbslotGameLoginLink();
  //WE FISH
  const { mutate: redirectToWeFish } = useGetWefishGameLoginLink();
  //WE SLOT
  const { mutate: redirectToWeSlot } = useGetWeslotGameLoginLink();
  //FCFISH
  const { mutate: redirectToFCFish } = useGetFCFishGameLoginLink();
  //JDBFISH
  const { mutate: redirectToJDBFish } = useGetJDBFishGameLoginLink();
  //JDBSLOT
  const { mutate: redirectToJDBSlot } = useGetJDBSlotGameLoginLink();
  //ONE JDBFISH
  const { mutate: redirectToONEJDBFish } = useGetONEJDBFISHGameLoginLink();
  //ONE JDBSLOT
  const { mutate: redirectToONEJDBSlot } = useGetONEJDBSLOTGameLoginLink();
  //ONE PP
  const { mutate: redirectToONEPP } = useGetONEPPGameLoginLink();
  //ONE PG
  const { mutate: redirectToONEPG } = useGetONEPGGameLoginLink();
  //ONE HACKSAW
  const { mutate: redirectToONEHACKSAW } = useGetONEHACKSAWGameLoginLink();
  //ONE JILIFISH
  const { mutate: redirectToONEJILIFISH } = useGetONEJILIFISHGameLoginLink();
  //ONE JILISLOT
  const { mutate: redirectToONEJILISLOT } = useGetONEJILISLOTGameLoginLink();
  //ONE JILICARD
  const { mutate: redirectToONEJILICARD } = useGetONEJILICARDGameLoginLink();
  //ONE JILICASINO
  const { mutate: redirectToONEJILICASINO } = useGetONEJILICASINOGameLoginLink();
  //ONE EVO LIVE
  const { mutate: redirectToONEEVOLIVE } = useGetONEEVOLIVEGameLoginLink();
  //ONE PPLIVE
  const { mutate: redirectToONEPPLIVE } = useGetONEPPLIVEGameLoginLink();
  //ONE SABA
  const { mutate: redirectToONESABA } = useGetONESABAGameLoginLink();
  //ONE GLOBAL
  const { mutate: redirectToOne } = useGetONEGLOBALGameLoginLink();
  //AG
  const { mutate: redirectToAg } = useGetAgGameLoginLink();
  //AG 試玩
  const { mutate: redirectToAgDemo } = useGetAgTrialGameLink();
  //OMG
  const { mutate: redirectToOmg } = useGetOmgGameLoginLink();
  //OMG 試玩
  const { mutate: redirectToOmgDemo } = useGetOmgDemoGameLink();
  //SBO SPORT
  const { mutate: redirectToSBOSport } = useGetSBOSPORTGameLoginLink();
  //ONE SABA
  const { mutate: redirectToCRSport } = useGetCRSPORTGameLoginLink();
  //ONE SABA
  const { mutate: redirectToFBSport } = useGetFBSPORTGameLoginLink();
  //VIACASINOLIVE
  const { mutate: redirectToViaCasinoLive } = useGetViaCasinoLiveGameLoginLink();
  //FFFLIVE
  const { mutate: redirectToFFFLive } = useGetFFFLiveGameLoginLink();
  //WCF
  const { mutate: redirectToWCF } = useGetWCFGameLoginLink();
  //MINIGAME
  const { mutate: redirectToMiniGame } = useGetMiniGameGameLoginLink();
  //MG -> special using GET
  const { refetch: getMGLoginLink, isFetching: isFetchingMgLink } = useGetMgGameLoginLink(
    { content_code: mgGameCode },
    {
      enabled: false,
      onSuccess: (data) => {
        setIsLoading(false);
        setGameURL(data?.url);
        setShowHeader(false);
        setShowBubble(true);
        setShowGamePopup(true);
      },
      onError: (e) => {
        // Toast.show({ icon: "fail", content: e });
        setGameURL(undefined);
        setIsLoading(false);
      },
    },
  );

  const { isHavePixelIntegration, pixelCreateCustomTracker } = useFacebookPixel();

  const [showTestAccountWarning, setShowTestAccountWarning] = useState(false);

  const [showTutorial, setShowTutorial] = useState(false);
  const [showLotteryButton, setShowLotteryButton] = useState(false);
  const [title, setTitle] = useState("");
  const [game3thName, setGame3thName] = useState("");

  const getTutorialSetting = () => {
    const _localSettings = JSON.parse(localStorage.getItem(localStorageKey.LOCAL_SETTINGS));
    if (!_localSettings) return [];
    const _userLocalSettings = _localSettings[userInfo.account_id];
    if (!_userLocalSettings || !_userLocalSettings.skip_tutorial) return [];
    return _userLocalSettings.skip_tutorial.split(",");
  };

  // 未登入試玩：OMG 不經後端，直接跟 OMG 拿試玩 gameurl；AG 走 AG 後端試玩商戶
  // 遊戲不支援試玩時改跳登入提示
  const handleDemoGame = ({ game3th, gameItem }) => {
    const isAg = isAgDemoGame(game3th.game_3th_code);
    const redirectToDemo = isAg ? redirectToAgDemo : redirectToOmgDemo;
    setIsLoading(true);
    redirectToDemo(
      { gameId: gameItem.game_id, lang: nowLang },
      {
        onSuccess: (success) => {
          setGameURL(
            isAg && !success.includes("lang")
              ? `${success}${success.includes("?") ? "&" : "?"}lang=${nowLang}`
              : success,
          );
          setIsLoading(false);
          setTitle(t(`games.fullName.${game3th.game_3th_code}`));
          setGame3thName(game3th.game_3th_code);
          setShowGamePopup(true);
        },
        onError: (error) => {
          setIsLoading(false);
          if (error?.notSupported) {
            checkIfLogin();
            return;
          }
          toast({ content: error?.message ?? error, type: "error" });
        },
      },
    );
  };

  const handleLoginGame = async ({ isPopup, isFromSearch, game3th, gameItem }) => {
    if (userInfo?.billing_type === 0) {
      if (game3th.allow_test_account === 0 || gameItem?.allow_test_account === 0) {
        setShowTestAccountWarning(true);
        return;
      }
    }

    if (gameItem) {
      setRecentPlayGameList({ game: { ...gameItem, is_recent: 1 } });
    }
    if (
      !getTutorialSetting().includes(game3th.game_3th_code) &&
      gameListsEnum[game3th.game_3th_code].haveTutorial === true
    ) {
      setGame3thName(game3th.game_3th_code);
      setShowTutorial(true);
    }

    if (
      gameListsEnum[game3th.game_3th_code].showLotteryButton === true &&
      envChangeType("REACT_APP_SHOW_LOTTERY_BUTTON", envType.BOOLEAN)
    ) {
      setShowLotteryButton(true);
    }

    if (isHavePixelIntegration) {
      pixelCreateCustomTracker({
        eventId: "enterGame",
        props: {
          platform: game3th.game_3th_code,
          game_code: gameItem?.game_code ? gameItem?.game_code : "",
        },
      });
    }

    switch (game3th.game_3th_code) {
      case "t1": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game: game3th.game_3th_code,
          game_code: gameItem.game_code,
        };
        setIsLoading(true);
        await redirectToT1(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(`${success}`);
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "evo": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game: game3th.game_3th_code,
          game_code: gameItem?.game_code ? gameItem.game_code : "",
          game_id: gameItem?.game_id,
        };
        setIsLoading(true);
        await redirectToEvo(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "pp": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
        };
        setIsLoading(true);
        await redirectToPp(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "pg": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToPg(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameHTML(success);
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "ygg": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToYgg(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "evoplay": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToEvoplay(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "jili": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToJili(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "jdb": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToSpribe(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "pt": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToPT(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "pplive": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToPPLive(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "fc": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: "FC",
          game_id: gameItem.game_id,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToFC(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "cq9": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: "CQ9",
          game_id: gameItem.game_id,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToCQ9(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "playstar": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToPlaystar(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "welive": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToWeLive(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "weblockchain": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToWeBlockchain(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "welottery": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToWeLottery(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "tcg": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToTCG(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "jilifish": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToJili(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "youbet": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToYoubet(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "aesexy": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToAeSexy(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "spribeone": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: "spribeone",
          game_id: gameItem.game_id,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToSpribeone(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "wesport": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: "SPORT-WEB_SOCCER",
          // game_code: gameItem.game_code,
          // game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToWesport(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "luckysport": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: "SPORT-WEB_SOCCER",
          // game_code: gameItem.game_code,
          // game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToLuckySport(
          { params: submitReq },
          {
            onSuccess: (success) => {
              // navigate("/luckysport#/", {
              //   state: {
              //     access_token: success.access_token,
              //   },
              // });
              // setGameHTML(success.widget_loader_script);
              setIsLoading(false);
              setGameURL(success.url);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "ybslot": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: "YBNGO_1_1038",
          game_id: gameItem.game_id,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToYBSlot(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "wefish": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToWeFish(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "weslot": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToWeSlot(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "fcfish": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToFCFish(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "jdbfish": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToJDBFish(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "jdbslot": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToJDBSlot(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "viacasinolive": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
        };
        setIsLoading(true);
        await redirectToViaCasinoLive(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "ffflive": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
        };
        setIsLoading(true);
        await redirectToFFFLive(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "wcf": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
        };
        setIsLoading(true);
        await redirectToWCF(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "saba": {
        const submitReq = {
          //1 pc 2 mobile
          platform: 2,
          game: game3th.game_3th_code,
        };
        setIsLoading(true);
        await redirectToGame(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              setShowTutorial(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "sabacricket": {
        const submitReq = {
          //1 pc 2 mobile
          platform: 2,
          game: game3th.game_3th_code,
          sport_id: 50,
        };
        setIsLoading(true);
        await redirectToGame(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              setShowTutorial(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "jollysport": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
        };
        setIsLoading(true);
        await redirectToJollySport(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success}` +
                  (success.includes("lang")
                    ? ""
                    : `${success.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "onejdbslot": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          lobby_url: window.location.origin,
          game_id: gameItem.game_id,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToONEJDBSlot(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "onejdbfish": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          lobby_url: window.location.origin,
          game_id: gameItem.game_id,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToONEJDBFish(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }

      case "onepp": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          lobby_url: window.location.origin,
          game_id: gameItem.game_id,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToONEPP(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "onepg": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          lobby_url: window.location.origin,
          game_id: gameItem.game_id,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToONEPG(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "onehacksaw": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          lobby_url: window.location.origin,
          game_id: gameItem.game_id,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToONEHACKSAW(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "onejilislot": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          lobby_url: window.location.origin,
          game_id: gameItem.game_id,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToONEJILISLOT(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "onejilifish": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          lobby_url: window.location.origin,
          game_id: gameItem.game_id,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToONEJILIFISH(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "onejilicard": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          lobby_url: window.location.origin,
          game_id: gameItem.game_id,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToONEJILIFISH(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "onejilicasino": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          lobby_url: window.location.origin,
          game_id: gameItem.game_id,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToONEJILICASINO(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "oneevolive": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem?.game_code
            ? gameItem.game_code
            : envChangeType("REACT_APP_EVO_BANNER_GAMECODE"),
          lobby_url: window.location.origin,
          game_id: gameItem?.game_id
            ? gameItem.game_id
            : envChangeType("REACT_APP_EVO_BANNER_GAMECODE"),
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToONEEVOLIVE(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "onepplive": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem?.game_code
            ? gameItem.game_code
            : envChangeType("REACT_APP_PP_BANNER_GAMECODE"),
          game_id: gameItem?.game_id
            ? gameItem.game_id
            : envChangeType("REACT_APP_PP_BANNER_GAMECODE"),
          lobby_url: window.location.origin,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToONEPPLIVE(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "oneaasexylive": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem?.game_code
            ? gameItem.game_code
            : envChangeType("REACT_APP_AA_SEXY_BANNER_GAMECODE"),
          game_id: gameItem?.game_id
            ? gameItem.game_id
            : envChangeType("REACT_APP_AA_SEXY_BANNER_GAMECODE"),
          lobby_url: window.location.origin,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToOne(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "onedblive": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem?.game_code
            ? gameItem.game_code
            : envChangeType("REACT_APP_DB_BANNER_GAMECODE"),
          game_id: gameItem?.game_id
            ? gameItem.game_id
            : envChangeType("REACT_APP_DB_BANNER_GAMECODE"),
          lobby_url: window.location.origin,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToOne(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "onepalive": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem?.game_code
            ? gameItem.game_code
            : envChangeType("REACT_APP_PA_BANNER_GAMECODE"),
          game_id: gameItem?.game_id
            ? gameItem.game_id
            : envChangeType("REACT_APP_PA_BANNER_GAMECODE"),
          lobby_url: window.location.origin,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToOne(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "onecq9live": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem?.game_code
            ? gameItem.game_code
            : envChangeType("REACT_APP_CQ9_BANNER_GAMECODE"),
          game_id: gameItem?.game_id
            ? gameItem.game_id
            : envChangeType("REACT_APP_CQ9_BANNER_GAMECODE"),
          lobby_url: window.location.origin,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToOne(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "onesaba": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToONESABA(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "onehabaneropoker":
      case "onekingmidaspoker":
      case "onekypoker":
      case "one365poker":
      case "onegtfslot":
      case "onemicroslot":
      case "onespadeslot":
      case "onerelaxslot":
      case "onehabaneroslot":
      case "onenetentslot":
      case "oneredtigerslot":
      case "onebigtimeslot":
      case "onenolimitcityslot":
      case "onebngslot":
      case "oneadvantplayslot":
      case "oneevoplayslot":
      case "onekingmidasslot":
      case "oneepicwinslot":
      case "onelive22slot":
      case "onecgslot":
      case "onedbslot":
      case "onethunderkickslot":
      case "onepaslot":
      case "oneplaytechslot":
      case "onealizeslot":
      case "oneavataruxslot":
      case "onethebetterslot":
      case "oneendorphinaslot":
      case "onelucky365slot":
      case "onewinfinitylive":
      case "oneyeebetlive":
      case "oneezugilive":
      case "onemicrolive":
      case "onewmlive":
      case "oneplaytechlive":
      case "onebiglive":
      case "onealizemini":
      case "oneaviatrixmini":
      case "onelitemini":
      case "oneaviatormini":
      case "onemicroarcade": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
          lobby_url: window.location.origin,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToOne(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "agjilislot":
      case "agpgslot":
      case "agppslot":
      case "agjdbslot":
      case "agbgslot":
      case "agfcslot":
      case "agcpslot":
      case "agwgslot":
      case "agtadaslot":
      case "aghsdslot": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToAg(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "omgspribe":
      case "omgpgslot":
      case "omgppslot":
      case "omghacksaw":
      case "omgjilislot":
      case "omgminigame":
      case "omgtadaslot":
      case "omgcpslot":
      case "omgaskmeslot":
      case "omglottery":
      case "omgcasino":
      case "omgjdbslot":
      case "omgwgslot":
      case "omg759slot":
      case "omgredtiger":
      case "omgfcslot":
      case "omgigtslot":
      case "omg2jgame":
      case "omgslotmill":
      case "omggameartslot": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          game_code: gameItem.game_code,
          game_id: gameItem.game_id,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToOmg(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "sbosport": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          lobby_url: window.location.origin,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToSBOSport(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "crsport": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          lobby_url: window.location.origin,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToCRSport(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "fbworldcup":
      case "fbsport": {
        const submitReq = {
          account_id: userInfo.account_id,
          account: userInfo.account,
          billing_type: userInfo.billing_type,
          lobby_url: window.location.origin,
          platform: "H5",
        };
        setIsLoading(true);
        await redirectToFBSport(
          { params: submitReq },
          {
            onSuccess: (success) => {
              setGameURL(
                `${success.gameUrl}` +
                  (success.gameUrl.includes("lang")
                    ? ""
                    : `${success.gameUrl.includes("?") ? "&" : "?"}lang=${nowLang}`),
              );
              setIsLoading(false);
              setTitle(t(`games.fullName.${game3th.game_3th_code}`));
              setGame3thName(game3th.game_3th_code);
              setShowGamePopup(true);
            },
            onError: (error) => {
              setIsLoading(false);
              toast({ content: error, type: "error" });
            },
          },
        );
        break;
      }
      case "77lottery": {
        setGameURL(
          `${gameListsEnum[game3th.game_3th_code].gameRoutePrefix}/${
            gameItem?.game_code ? `lottery/${gameItem?.game_code}` : ""
          }?token=${token?.replace(/\"/gm, "")}&lang=${nowLang}&currency=${currency}`,
        );
        setTitle(
          t([
            `games.fullName.${envChangeType("REACT_APP_PRODUCTION_NAME_DEV")}.${game3th.game_3th_code}`,
            `games.fullName.${game3th.game_3th_code}`,
          ]),
        );
        setGame3thName(game3th.game_3th_code);
        setShowGamePopup(true);
        break;
      }
      case "minigame": {
        setGameURL(
          `${
            gameListsEnum[game3th.game_3th_code].gameRoutePrefix
          }?game_code=${gameItem?.game_code.toLowerCase()}&token=${token?.replace(
            /\"/gm,
            "",
          )}&lang=${nowLang}&currency=${currency}`,
        );

        //>>>>>TEMPORARY
        // setGameURL(
        //   `${
        //     gameListsEnum[game3th.game_3th_code].gameRoutePrefix
        //   }?game_code=flygo&token=${token?.replace(
        //     /\"/gm,
        //     "",
        //   )}&lang=${nowLang}&currency=${currency}`,
        // );

        setTitle(
          t([
            `games.fullName.${envChangeType("REACT_APP_PRODUCTION_NAME_DEV")}.${game3th.game_3th_code}`,
            `games.fullName.${game3th.game_3th_code}`,
          ]),
        );
        setGame3thName(game3th.game_3th_code);
        setShowGamePopup(true);
        break;
      }
      case "winlottery": {
        setGame3thName(game3th.game_3th_code);
        navigate(
          `${
            gameListsEnum[game3th.game_3th_code].gameRoutePrefix ?? "/"
          }${gameItem.game_code?.toLowerCase()}?tab=${gameItem.game_id}`,
          {
            state: {
              isPopup: isPopup,
              isFromSearch: isFromSearch,
            },
          },
        );
        break;
      }
      case "mg": {
        setGame3thName(game3th.game_3th_code);
        setMGGameCode(gameItem.game_code);
        setIsLoading(true);
        break;
      }
      default:
        break;
    }
  };

  const safeTutorialSetting = (game3thName) => {
    const _localSettings = JSON.parse(localStorage.getItem(localStorageKey.LOCAL_SETTINGS)) || {};
    let _userLocalSettings = {};
    if (_localSettings) {
      _userLocalSettings = _localSettings[userInfo.account_id];
    }

    let newClosedTutorial = game3thName;
    if (!_userLocalSettings) {
      const userSetting = {
        ..._localSettings,
        [userInfo.account_id]: {
          ..._userLocalSettings,
          skip_tutorial: newClosedTutorial,
        },
      };
      localStorage.setItem(localStorageKey.LOCAL_SETTINGS, JSON.stringify(userSetting));
    } else {
      const currentClosedTutorial = _userLocalSettings?.skip_tutorial || "";
      newClosedTutorial = `${newClosedTutorial},${currentClosedTutorial}`;
      const newUserSetting = {
        ...[userInfo.account_id],
        skip_tutorial: newClosedTutorial,
      };
      localStorage.setItem(localStorageKey.LOCAL_SETTINGS, JSON.stringify(newUserSetting));
    }
  };

  useEffect(() => {
    if (mgGameCode) {
      getMGLoginLink();
    }
  }, [mgGameCode]);

  useEffect(() => {
    if (
      location.pathname !== "/" ||
      envChangeType("REACT_APP_CHATWOOT_ICON_IS_CUSTOMIZED", envType.BOOLEAN)
    )
      return;
    const syncChatwootBubble = () => {
      safeToggleChatwootBubble(showGamePopup ? "hide" : "show");
    };
    syncChatwootBubble();
    window.addEventListener("chatwoot:ready", syncChatwootBubble);
    return () => window.removeEventListener("chatwoot:ready", syncChatwootBubble);
  }, [showGamePopup, location.pathname]);

  const resetState = () => {
    cache.invalidateQueries([queryKey.USER_WALLET]);
    setShowGamePopup(false);
    setGameURL("");
    setTitle("");
    setShowHeader(true);
    setShowBubble(false);
    setMGGameCode(null);
    setGame3thName("");
    setShowTutorial(false);
    setShowLotteryButton(false);
  };

  return (
    <GameLoginContext.Provider
      value={{
        handleLoginGame,
        handleDemoGame,
        showGamePopup,
        setShowGamePopup,
      }}
    >
      {children}
      <GamePopup
        title={title}
        visible={showGamePopup}
        onClose={resetState}
        showHeader={showHeader}
        showBubble={showBubble}
        showTutorial={showTutorial}
        setShowTutorial={setShowTutorial}
        gameURL={gameURL}
        gameHTML={gameHTML}
        game3thName={game3thName}
        closeTutorial={(isChecked, game3thName) => {
          setShowTutorial(false);
          if (isChecked === true) {
            safeTutorialSetting(game3thName);
          }
        }}
        showLotteryButton={showLotteryButton}
      />
      <CustomModal
        isModalVisible={showTestAccountWarning}
        setIsModalVisible={setShowTestAccountWarning}
        title={t([
          `${envChangeType("REACT_APP_PRODUCTION_NAME_DEV")}.popup.notAllowTest.desc`,
          "1bbet.popup.notAllowTest.desc",
        ])}
        content={""}
        needIcon={true}
        okBtnText={t("normal.confirm")}
        isHaveCancel={false}
      />
    </GameLoginContext.Provider>
  );
};
export const useGameLogin = () => {
  const GameLoginValue = useContext(GameLoginContext);
  return GameLoginValue;
};
